import { MKHeaders } from "../../alias";
import { MKAdobePrimetimeConfig, MKError, MKHttpResponse, MKSessionConcurrencyAPI } from "../../interfaces";
import { MKLog, MKUtils } from "../common";
import { MKAdobeSessionConcurrencyError } from "../error";
import { MKHttpRequest } from "./MKHttpRequest";

/**
 * Adobe Session Concurrency Request
 * @internal
 */
export class MKAdobeSessionConcurrencyRequest extends MKHttpRequest implements MKSessionConcurrencyAPI {
    // Set the session manager to adobe
    public sessionManager: "session_manager_adobe" | "session_manager_azuki" = "session_manager_adobe";
    public isPaused: boolean = false;
    private static readonly DEFAULT_PRODUCTION_ENDPOINT = "https://streams.adobeprimetime.com/v2/sessions";
    private static readonly DEFAULT_STAGING_ENDPOINT = "https://streams-stage.adobeprimetime.com/v2/sessions";
    private static readonly DEFAULT_TIMER_FREQUENCY = 10 * 1000; // 10 seconds
    private session: string;
    private config: MKAdobePrimetimeConfig;
    private heartBeatTimer?: ReturnType<typeof setTimeout>;
    private heartBeatFrequency: number;
    private errorCallback?: (error: MKError) => void;

    /**
     * Adobe Session Concurrency Request.
     * @param config the adobe primetime configuration object.
     * @param errorCallback error callback to get notified when error occurs during heart beat (session concurrency) notifications
     */
    constructor(config: MKAdobePrimetimeConfig, errorCallback?: (error: MKError) => void) {
        super();
        this.session = "";
        this.heartBeatFrequency = MKAdobeSessionConcurrencyRequest.DEFAULT_TIMER_FREQUENCY;
        this.config = this.validateConfig(config);
        this.errorCallback = errorCallback;
    }

    /**
     * Start a new adobe primetime concurrency monitoring session.
     * @returns a promise which will resolve on success and reject on error with appropriate error code and message.
     */
    async startSession(): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            // stop the session is already running before starting a new one
            if (!!this.session) {
                await this.stopSession()
                    .catch((error) => {
                        return reject(error);
                    });
            }

            this.isPaused = false;

            // session init
            this.post(this.getRequestUrl(), { headers: this.getRequestHeaders(true) })
                .then((response: MKHttpResponse) => {
                    if (response.status === 201 || response.status === 202 || response.status === 203) {
                        if (response.headers && typeof response.headers === "object") {
                            if ("location" in response.headers && !!response.headers["location"]) {
                                this.session = response.headers["location"];
                                MKLog.debug(`[Adobe] new session ${this.session} created!`);

                                // get the heart beat timer frequency and schedule heart beats
                                const currentTime = Date.now();
                                const expiry = "expires" in response.headers && !!response.headers["expires"] ? Date.parse(response.headers["expires"]) : 0;
                                this.heartBeatFrequency = (expiry - currentTime);
                                this.heartBeatFrequency = this.heartBeatFrequency > 0 ? this.heartBeatFrequency : MKAdobeSessionConcurrencyRequest.DEFAULT_TIMER_FREQUENCY;
                                MKLog.debug(`[Adobe] session heart beat frequency ${this.heartBeatFrequency / 1000} seconds`);
                                this.scheduleHeartBeat(this.heartBeatFrequency);
                            } else {
                                MKLog.warn("[Adobe] no session in response, heart beat will not be scheduled for this session!");
                            }
                        } else {
                            MKLog.warn("[Adobe] Invalid headers in response, heart beat will not be scheduled for this session!");
                        }
                        return resolve();
                    } else {
                        // For any 4xx or 5xx errors
                        return reject(new MKAdobeSessionConcurrencyError({ code: response.status, message: response.statusText }));
                    }
                })
                .catch((error: any) => {
                    // typically HTTP Errors due to invalid url or related
                    return reject(error);
                });
        });
    }

    /**
     * Pause the adobe primetime calls.
     * Mainly used to pause the calls while the playback is being casted.
     */
    PauseSession(): void {
        this.isPaused = true;
        // stop the timer
        clearInterval(this.heartBeatTimer);
    }

    /**
     * Stop the ongoing adobe primetime concurrency monitoring session.
     * @returns a promise which will resolve on success and reject on error with appropriate error code and message.
     */
    async stopSession(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!!!this.session) return resolve();

            // session termination
            this.delete(this.getRequestUrl(), { headers: this.getRequestHeaders() })
                .then((response: MKHttpResponse) => {
                    if (response.status === 202 || response.status === 204) {
                        MKLog.debug(`[Adobe] session ${this.session} stopped!`);
                        this.session = "";
                        this.heartBeatFrequency = MKAdobeSessionConcurrencyRequest.DEFAULT_TIMER_FREQUENCY;
                        clearInterval(this.heartBeatTimer);
                        return resolve();
                    } else {
                        // For any 4xx or 5xx errors
                        return reject(new MKAdobeSessionConcurrencyError({ code: response.status, message: response.statusText }));
                    }
                })
                .catch((error) => {
                    // typically HTTP Errors due to invalid url or related
                    return reject(error);
                });
        });
    }

    /**
     * Send adobe concurrency monitoring heartbeat for current session.
     * @returns a promise which will resolve on success and reject on error with appropriate error code and message.
     */
    private async notifyHeartBeat(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!!!this.session) return resolve();

            // heart beat
            this.post(this.getRequestUrl(), { headers: this.getRequestHeaders() })
                .then((response: MKHttpResponse) => {
                    // it's possible that the session could have been terminated when this request went out so we will
                    // check for a valid session once again and then process the response if we still have a valid session.
                    if (!!!this.session) return resolve();
                    if (response.status === 202 || response.status === 203 || response.status === 204) {
                        MKLog.debug("[Adobe] session heart beat notification successful!");
                        if (response.headers && typeof response.headers === "object") {
                            // update the heart beat timer frequency if changed and re-schedule heart beats
                            const currentTime = Date.now();
                            const expiry = "expires" in response.headers && !!response.headers["expires"] ? Date.parse(response.headers["expires"]) : 0;
                            let heartBeatFrequency = ((expiry - currentTime) / 1000);
                            heartBeatFrequency = this.heartBeatFrequency > 0 ? this.heartBeatFrequency : MKAdobeSessionConcurrencyRequest.DEFAULT_TIMER_FREQUENCY;
                            if (heartBeatFrequency !== this.heartBeatFrequency) {
                                this.heartBeatFrequency = heartBeatFrequency;
                                MKLog.debug(`[Adobe] session heart beat frequency updated to ${this.heartBeatFrequency / 1000} seconds`);
                                this.scheduleHeartBeat(this.heartBeatFrequency);
                            }
                        }
                        return resolve();
                    } else {
                        // For any 4xx or 5xx errors
                        return reject(new MKAdobeSessionConcurrencyError({ code: response.status, message: response.statusText }));
                    }
                })
                .catch((error) => {
                    // typically HTTP Errors due to invalid url or related
                    return reject(error);
                });
        });
    }

    /**
     * Schedule session concurrency heartbeats.
     * @param frequency the interval in seconds at which heart beat is to be notified
     */
    private scheduleHeartBeat(frequency: number): void {
        if (this.heartBeatTimer) clearInterval(this.heartBeatTimer);
        this.heartBeatTimer = setInterval(() => {
            this.notifyHeartBeat()
                .catch((error) => {
                    // stop timer before notifying error
                    clearInterval(this.heartBeatTimer);
                    if (this.errorCallback) this.errorCallback(error);
                });
        }, frequency);
    }

    /**
     * Verify the given adobe primetime configuration.
     * @param config the given adobe primetime configuration object
     * @returns the verified configuration object or throws an error when the given object is invalid
     */
    private validateConfig(config: MKAdobePrimetimeConfig): MKAdobePrimetimeConfig {
        if (!!!config.endpointUrl) {
            // use default staging or production endpointUrl
            config.endpointUrl = config.isStaging === undefined || config.isStaging === true ? MKAdobeSessionConcurrencyRequest.DEFAULT_STAGING_ENDPOINT : MKAdobeSessionConcurrencyRequest.DEFAULT_PRODUCTION_ENDPOINT;
        }
        if (!!!config.endpointUrl || !!!config.mvpd || !!!config.upstreamUserId || !!!config.applicationId) {
            throw new Error("Invalid adobe primetime configuration!");
        }
        return config;
    }

    /**
     * Get the request URL.
     * @returns the constructed request URL.
     */
    private getRequestUrl(): string {
        const requestUrl = `${this.config.endpointUrl}/${this.config.mvpd}/${this.config.upstreamUserId}`;
        if (!!this.session) {
            return `${requestUrl}/${this.session}`;
        }
        return requestUrl;
    }

    /**
     * Get the request headers.
     * @returns the necessary request headers.
     */
    private getRequestHeaders(urlencoded: boolean = false): MKHeaders {
        const username = this.config.applicationId;
        const loginEncodedString = MKUtils.base64Encode(username);
        return {
            "Accept": "application/json",
            "Content-Type": urlencoded === false ? "application/json" : "application/x-www-form-urlencoded",
            "Authorization": `Basic ${loginEncodedString}`
        };
    }
}